import { useEffect, useRef } from 'react';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';

const getDialogIdentifier = dialog => {
    const dataId = dialog.dataset.dialogId;
    if (dataId) {
        return dataId;
    }

    const hash = globalThis.location.hash;
    const pathname = globalThis.location.pathname?.replace(/^\//, '') || '';

    if (hash) {
        if (hash.includes('?')) {
            const hashParts = hash.split('?');
            if (hashParts.length > 1) {
                return hashParts[1];
            }
        } else {
            const hashValue = hash.substring(1);
            return pathname ? `${pathname}-${hashValue}` : hashValue;
        }
    }

    return pathname || 'default';
};

const getStorageKey = identifier => `dialog-width-${identifier}`;

const saveDialogWidth = (identifier, width) => {
    try {
        localStorage.setItem(getStorageKey(identifier), width.toString());
    } catch (error) {
        console.warn('Failed to save dialog width to localStorage:', error);
    }
};

const getSavedDialogWidth = identifier => {
    try {
        const saved = localStorage.getItem(getStorageKey(identifier));
        return saved ? parseInt(saved, 10) : null;
    } catch (error) {
        console.warn('Failed to read dialog width from localStorage:', error);
        return null;
    }
};

const getInitialMinWidth = dialog => {
    const computedWidth = dialog.offsetWidth || Number.parseInt(globalThis.getComputedStyle(dialog).width, 10);
    const cssMinWidth = 540;
    return computedWidth || cssMinWidth;
};

export const useDialogResizer = () => {
    const isResizingRef = useRef(false);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);
    const dialogRef = useRef(null);

    const isDesktop = () => globalThis.innerWidth >= breakpoints.values.lg;

    useEffect(() => {
        const handleMouseDown = (e, dialog) => {
            if (!e.target.classList.contains('dialog-resizer-handle')) {
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            isResizingRef.current = true;
            startXRef.current = e.clientX;
            dialogRef.current = dialog;
            startWidthRef.current = dialog.offsetWidth;

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
        };

        const handleMouseMove = e => {
            if (!isResizingRef.current || !dialogRef.current) {
                return;
            }

            const initialMinWidth = dialogRef.current.getAttribute('data-initial-width')
                ? parseInt(dialogRef.current.getAttribute('data-initial-width'), 10)
                : getInitialMinWidth(dialogRef.current);

            const deltaX = startXRef.current - e.clientX;
            const newWidth = startWidthRef.current + deltaX;

            const maxWidth = globalThis.innerWidth - 23;

            if (newWidth >= initialMinWidth && newWidth <= maxWidth) {
                dialogRef.current.style.width = `${newWidth}px`;
                dialogRef.current.style.minWidth = `${newWidth}px`;
                dialogRef.current.style.maxWidth = `${newWidth}px`;
                dialogRef.current.style.right = '0';
                dialogRef.current.style.position = 'fixed';
            } else if (newWidth < initialMinWidth) {
                dialogRef.current.style.width = `${initialMinWidth}px`;
                dialogRef.current.style.minWidth = `${initialMinWidth}px`;
                dialogRef.current.style.maxWidth = `${initialMinWidth}px`;
                dialogRef.current.style.right = '0';
                dialogRef.current.style.position = 'fixed';
            }
        };

        const handleMouseUp = () => {
            if (isResizingRef.current && dialogRef.current) {
                const identifier = getDialogIdentifier(dialogRef.current);
                const finalWidth = dialogRef.current.offsetWidth;
                saveDialogWidth(identifier, finalWidth);

                isResizingRef.current = false;
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                dialogRef.current = null;
            }
        };

        const removeResizerFromDialogs = () => {
            const dialogs = document.querySelectorAll('.custom-dialog-right');
            dialogs.forEach(dialog => {
                const resizerHandle = dialog.querySelector('.dialog-resizer-handle');
                if (resizerHandle) {
                    resizerHandle.remove();
                }
            });
        };

        const addResizerToDialogs = () => {
            if (!isDesktop()) {
                removeResizerFromDialogs();
                return;
            }

            const dialogs = document.querySelectorAll('.custom-dialog-right');

            dialogs.forEach(dialog => {
                if (dialog.classList.contains('custom-dialog-right--no-resizer')) {
                    const resizerHandle = dialog.querySelector('.dialog-resizer-handle');
                    if (resizerHandle) {
                        resizerHandle.remove();
                    }
                    return;
                }

                if (dialog.querySelector('.dialog-resizer-handle')) {
                    return;
                }

                const identifier = getDialogIdentifier(dialog);
                const savedWidth = getSavedDialogWidth(identifier);

                const initialWidth = dialog.offsetWidth || Number.parseInt(globalThis.getComputedStyle(dialog).width, 10) || 540;

                if (!dialog.dataset.initialWidth) {
                    dialog.dataset.initialWidth = initialWidth.toString();
                }

                if (savedWidth) {
                    const initialMinWidth = Number.parseInt(dialog.dataset.initialWidth, 10) || initialWidth;

                    const maxWidth = globalThis.innerWidth - 23;
                    const width = Math.max(initialMinWidth, Math.min(maxWidth, savedWidth));

                    dialog.style.width = `${width}px`;
                    dialog.style.minWidth = `${width}px`;
                    dialog.style.maxWidth = `${width}px`;
                } else {
                    dialog.style.width = `${initialWidth}px`;
                    dialog.style.minWidth = `${initialWidth}px`;
                    dialog.style.maxWidth = `${initialWidth}px`;
                }

                const resizerHandle = document.createElement('div');
                resizerHandle.className = 'dialog-resizer-handle';
                dialog.style.right = '0';
                dialog.style.position = 'fixed';
                dialog.appendChild(resizerHandle);

                resizerHandle.addEventListener('mousedown', e => handleMouseDown(e, dialog));
            });
        };

        addResizerToDialogs();

        const handleWindowResize = () => {
            addResizerToDialogs();
        };

        const updateDialogWidth = (dialog, identifier) => {
            const savedWidth = getSavedDialogWidth(identifier);
            const initialWidth = dialog.dataset.initialWidth || dialog.offsetWidth || Number.parseInt(globalThis.getComputedStyle(dialog).width, 10) || 540;
            const maxWidth = globalThis.innerWidth - 23;
            const targetWidth = savedWidth ? Math.max(initialWidth, Math.min(maxWidth, savedWidth)) : initialWidth;

            dialog.style.width = `${targetWidth}px`;
            dialog.style.minWidth = `${targetWidth}px`;
            dialog.style.maxWidth = `${targetWidth}px`;
        };

        const ensureInitialWidth = dialog => {
            if (!dialog.dataset.initialWidth) {
                const newInitialWidth = dialog.offsetWidth || Number.parseInt(globalThis.getComputedStyle(dialog).width, 10) || 540;
                dialog.dataset.initialWidth = newInitialWidth.toString();
            }
        };

        const updateDialogOnUrlChange = dialog => {
            const identifier = getDialogIdentifier(dialog);
            const previousIdentifier = dialog.dataset.dialogIdentifier;

            if (previousIdentifier && previousIdentifier === identifier) {
                return;
            }

            ensureInitialWidth(dialog);
            updateDialogWidth(dialog, identifier);
            dialog.dataset.dialogIdentifier = identifier;
        };

        const handleUrlChange = () => {
            setTimeout(() => {
                const dialogs = document.querySelectorAll('.custom-dialog-right');
                dialogs.forEach(updateDialogOnUrlChange);
                addResizerToDialogs();
            }, 50);
        };

        const observeUrlChanges = () => {
            let lastHash = globalThis.location.hash;
            let lastPathname = globalThis.location.pathname;
            let lastSearch = globalThis.location.search;

            const checkUrlChange = () => {
                const currentHash = globalThis.location.hash;
                const currentPathname = globalThis.location.pathname;
                const currentSearch = globalThis.location.search;

                if (currentHash !== lastHash || currentPathname !== lastPathname || currentSearch !== lastSearch) {
                    lastHash = currentHash;
                    lastPathname = currentPathname;
                    lastSearch = currentSearch;
                    handleUrlChange();
                }
            };

            const urlCheckInterval = setInterval(checkUrlChange, 100);

            const handleHashChange = () => {
                checkUrlChange();
            };

            const handlePopState = () => {
                checkUrlChange();
            };

            globalThis.addEventListener('hashchange', handleHashChange);
            globalThis.addEventListener('popstate', handlePopState);

            return () => {
                clearInterval(urlCheckInterval);
                globalThis.removeEventListener('hashchange', handleHashChange);
                globalThis.removeEventListener('popstate', handlePopState);
            };
        };

        const urlObserverCleanup = observeUrlChanges();

        globalThis.addEventListener('resize', handleWindowResize);

        const observer = new MutationObserver(() => {
            addResizerToDialogs();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
            urlObserverCleanup();
            globalThis.removeEventListener('resize', handleWindowResize);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);
};
